import { ComponentProps, useEffect } from 'react';
import {
    createInstance,
    OptimizelyProvider,
    ReactSDKClient,
    enums
} from '@optimizely/react-sdk';
import { setLogLevel } from '@optimizely/optimizely-sdk';

import { OptimizelyOverrides } from './optimizelyOverrides';
import triggerTracking from '@blueprint/tracking';

export type OptimizelyDataFile = Parameters<
    typeof createInstance
>[0]['datafile'];

export type OptimizelyUserInfo = ComponentProps<
    typeof OptimizelyProvider
>['user'];

export const getOptimizelyFeatureFlag = (
    featureFlagName: string,
    featureFlagOverrides: OptimizelyOverrides['features'],
    optimizelyClient: ReactSDKClient | null
): boolean => {
    if (featureFlagName in featureFlagOverrides) {
        return featureFlagOverrides[featureFlagName];
    }
    return optimizelyClient?.isFeatureEnabled(featureFlagName) ?? false;
};

export const getOptimizelyFeatureFlagBooleanVariable = (
    featureFlagName: string,
    variableKey: string,
    optimizelyClient: ReactSDKClient | null
): boolean | null => {
    return (
        optimizelyClient?.getFeatureVariableBoolean(
            featureFlagName,
            variableKey
        ) ?? null
    );
};

export const createOptimizelyReactSDKClient = (
    datafile: OptimizelyDataFile
): ReactSDKClient =>
    // Todo: Correct the TS error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    /* @ts-ignore */
    createInstance({
        datafile,
        eventBatchSize: 10,
        eventFlushInterval: 1000
    });

export const getOptimizelyUserInfo = (
    zooplapsid: string | undefined,
    userAgent: string | undefined
): OptimizelyUserInfo => {
    if (zooplapsid === undefined) return;

    return {
        id: zooplapsid,
        attributes: {
            $opt_user_agent: userAgent
        }
    };
};

export const useTrackOptimizelyFeature = (
    feature: string,
    featureFlagOverrides: OptimizelyOverrides['features'],
    optimizelyClient: ReactSDKClient | null,
    featureEnabledLabel = 'alternative'
) => {
    const isFeatureEnabled = getOptimizelyFeatureFlag(
        feature,
        featureFlagOverrides,
        optimizelyClient
    );

    useEffect(() => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'optimizely_feature',
            eventAction: feature.toString(),
            eventLabel: isFeatureEnabled ? featureEnabledLabel : 'control'
        });
    }, [feature, featureEnabledLabel, isFeatureEnabled]);

    return isFeatureEnabled;
};

// Logging docs:
// https://docs.developers.optimizely.com/full-stack/docs/customize-logger-javascript
const isDev = process.env.NODE_ENV !== 'production';
if (isDev) {
    // Change here to increase logging levels in development
    setLogLevel(enums.LOG_LEVEL.WARNING);
}
